import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages, isSupportedLanguage } from '@/data/languages';

interface LanguageSwitcherProps {
  solid?: boolean;
}

export default function LanguageSwitcher({ solid = false }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const currentLang = isSupportedLanguage(firstSegment) ? firstSegment : 'en';

  function switchTo(code: string) {
    setOpen(false);
    const segments = location.pathname.split('/').filter(Boolean);
    if (isSupportedLanguage(segments[0])) {
      segments[0] = code;
    } else {
      segments.unshift(code);
    }
    navigate(`/${segments.join('/')}`);
  }

  const textColor = solid ? 'text-foreground-800' : 'text-white';
  const borderColor = solid ? 'border-background-200' : 'border-white/30';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-1.5 rounded-md border ${borderColor} px-3 py-2 text-sm font-medium ${textColor} transition-colors hover:opacity-80 cursor-pointer`}
      >
        <i className="ri-global-line text-base leading-none"></i>
        <span className="uppercase whitespace-nowrap">{currentLang}</span>
        <i className={`ri-arrow-down-s-line text-base leading-none transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-background-50 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => switchTo(lang.code)}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors hover:bg-background-100 cursor-pointer ${
                lang.code === currentLang ? 'text-primary-700 font-semibold' : 'text-foreground-800'
              }`}
            >
              <span className="whitespace-nowrap">{t(lang.labelKey)}</span>
              {lang.code === currentLang && <i className="ri-check-line text-base leading-none"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}